import type { MessageContent, ToolMessage } from "@langchain/core/messages";
import { Document } from "@models";

export type SelectableDataKey = {
  dataKey: string;
  document?: Document;
  documentTitle?: string;
  label?: string;
  source: "document" | "system";
};

export type ChatToolMessage = Pick<
  ToolMessage,
  "content" | "status" | "tool_call_id"
> &
  Partial<Pick<ToolMessage, "id" | "name" | "type" | "artifact">> & {
    role?: "tool";
  };

export type ChatMessageRole = "assistant" | "user" | "system" | "tool";
export type ChatMessageType = "ai" | "human" | "system" | "tool";

export type ChatToolCall = {
  type?: "tool_call" | "tool_call_chunk" | "invalid_tool_call";
  id?: string;
  name?: string;
  args?: unknown;
  error?: string;
};

export type ChatSkillContext = {
  id: string;
  name: string;
  path: string;
};

export const messageSkillContexts = (value: unknown): ChatSkillContext[] =>
  Array.isArray(value)
    ? value.flatMap((context) => {
        if (
          typeof context !== "object" ||
          context === null ||
          typeof (context as ChatSkillContext).id !== "string" ||
          typeof (context as ChatSkillContext).name !== "string" ||
          typeof (context as ChatSkillContext).path !== "string"
        ) {
          return [];
        }
        return [context as ChatSkillContext];
      })
    : [];

export type ChatMessage = {
  id?: string;
  type?: ChatMessageType | string;
  role?: ChatMessageRole | string;
  content: MessageContent;
  tool_calls?: ChatToolCall[];
  tool_call_id?: string;
  name?: string;
  status?: ChatToolMessage["status"];
  artifact?: ChatToolMessage["artifact"];
  lc_kwargs?: unknown;
  additional_kwargs?: {
    [key: string]: unknown;
    attachments?: import("./attachments").AttachmentReference[];
    skillContexts?: ChatSkillContext[];
  };
};

export type ChatSubmitMessage = ChatMessage & {
  role: ChatMessageRole;
};

export const getChatMessageType = (
  message: Pick<ChatMessage, "type" | "role"> & {
    _getType?: () => string;
  }
): ChatMessageType | undefined => {
  const classMessageType = message._getType?.();
  if (
    classMessageType === "ai" ||
    classMessageType === "human" ||
    classMessageType === "system" ||
    classMessageType === "tool"
  ) {
    return classMessageType;
  }

  if (
    message.type === "ai" ||
    message.type === "human" ||
    message.type === "system" ||
    message.type === "tool"
  ) {
    return message.type;
  }

  switch (message.role) {
    case "assistant":
      return "ai";
    case "user":
      return "human";
    case "system":
    case "tool":
      return message.role;
    default:
      return undefined;
  }
};
